import { Button, Form, Select, Typography } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import { Dayjs } from 'dayjs';
import React, { ReactElement, useCallback, useState } from 'react';
import WarehouseTypeEnum from '~Api/Warehouse/WarehouseTypeEnum';
import Layout from '~Reports/Layout';
import DatePicker from '~UI/DatePicker';
import './warehouses-cashflow.less';

interface IErrors {
    month?: string;
    warehouseType?: string;
}

export default function WarehousesCashflow(): ReactElement {
    const [ errors, setErrors ] = useState<IErrors>({});
    const [ month, setMonth ] = useState<Dayjs>(null);
    const [ warehouseType, setWarehouseType ] = useState<WarehouseTypeEnum>(null);

    const validateMonth: () => boolean = useCallback(() => {
        let error: string;

        if (!month) {
            error = 'Please choose a month';
        }

        setErrors((prevErrors: IErrors) => ({
            ...prevErrors,
            month: error,
        }));

        return !error;
    }, [
        month,
    ]);

    const onChangeMonth: (month: Dayjs) => void = useCallback((month: Dayjs) => {
        setMonth(month);
    }, []);

    const validateWarehouseType: () => boolean = useCallback(() => {
        let error: string = null;

        if (!warehouseType) {
            error = 'Please select a warehouse';
        }

        setErrors((prevErrors: IErrors) => ({
            ...prevErrors,
            warehouseType: error,
        }));

        return !error;
    }, [
        warehouseType,
    ]);

    const onChangeWarehouseType: (value: string) => void = useCallback((value: string) => {
        setWarehouseType(value as WarehouseTypeEnum);
    }, []);

    const onClickDownload: () => void = useCallback(() => {
        let valid: boolean = true;

        valid = validateMonth() && valid;
        valid = validateWarehouseType() && valid;

        if (!valid) {
            return;
        }

        switch (warehouseType) {
            case WarehouseTypeEnum.Fwt1:
            case WarehouseTypeEnum.Fwt2:
                window.open(`${process.env.API_HOST}/warehouse-reports/fwt-cashflow?month=${month.format('YYYY-MM')}&warehouseType=${warehouseType}`, '_blank');
                break;
            case WarehouseTypeEnum.Fit:
                window.open(`${process.env.API_HOST}/warehouse-reports/fit-cashflow?month=${month.format('YYYY-MM')}`, '_blank');
                break;
        }
    }, [
        month,
        validateMonth,
        validateWarehouseType,
        warehouseType,
    ]);

    const warehouseOptions: DefaultOptionType[] = [
        {
            label: 'Funding Investment Trust',
            value: WarehouseTypeEnum.Fit,
        },
        {
            label: 'Funding Warehouse Trust 1',
            value: WarehouseTypeEnum.Fwt1,
        },
        {
            label: 'Funding Warehouse Trust 2',
            value: WarehouseTypeEnum.Fwt2,
        },
    ];

    return (
        <Layout section='warehouses-cashflow'>
            <Typography.Title level={2}>Warehouses - Cashflow</Typography.Title>
            <Form.Item
                className='warehouse-type'
                help={errors.warehouseType}
                label='Warehouse'
                validateStatus={errors.warehouseType && 'error'}
            >
                <Select
                    value={warehouseType}
                    onChange={onChangeWarehouseType}
                    onBlur={validateWarehouseType}
                    options={warehouseOptions}
                />
            </Form.Item>
            <Form.Item
                className='month'
                help={errors.month}
                label='Month'
                validateStatus={errors.month && 'error'}
            >
                <DatePicker
                    picker='month'
                    format='MMMM YYYY'
                    onBlur={validateMonth}
                    onChange={onChangeMonth}
                    value={month}
                />
            </Form.Item>
            <Form.Item>
                <Button onClick={onClickDownload} type='primary'>Download</Button>
            </Form.Item>
        </Layout>
    );
}
